import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { QRCodeCanvas } from 'qrcode.react';
import saApi from '../../services/superAdminApi';
import {
  MdContentCopy, MdOpenInNew, MdRefresh, MdCheckCircle, MdCancel,
  MdInfo, MdPeople, MdRocketLaunch, MdDownload, MdEmail,
} from 'react-icons/md';
import { FaWhatsapp, FaFacebook, FaInstagram } from 'react-icons/fa';

// ── Source tracking helpers ───────────────────────────────────────────────────
const SOURCE_LABEL = {
  qr:        'QR Code',
  whatsapp:  'WhatsApp',
  facebook:  'Facebook',
  instagram: 'Instagram',
  messenger: 'Messenger',
  direct:    'Direct',
  email:     'Email',
  other:     'Other',
};

function sortedSources(breakdown) {
  return Object.entries(breakdown || {}).sort((a, b) => b[1] - a[1]);
}

// ── Share message templates ───────────────────────────────────────────────────
const EMAIL_SUBJECT = 'TrainAxis demo for your club';

const TEMPLATES = {
  whatsapp:  (l) =>
    `Namaste,\nHere is a quick TrainAxis demo for managing a taekwondo or fitness club in Nepal.\n\nYou can see how it handles students, attendance, monthly fees, QR payments, parent reminders, expenses, and reports.\n\nDemo:\n${l}?ref=whatsapp\n\nIf you want, we can help set this up for your club.`,
  facebook:  (l) =>
    `Run your taekwondo/gym/fitness club more easily with TrainAxis.\n\nManage:\n- Students\n- Attendance\n- Monthly fees\n- eSewa/Khalti/Fonepay QR payment proof\n- Parent reminders\n- Belt progress\n- Reports\n\nTry the demo here:\n${l}?ref=facebook`,
  instagram: (l) =>
    `Namaste 👋\nWe built TrainAxis to help Nepalese taekwondo, martial arts, and fitness clubs manage students, attendance, fees, QR payments, and parent reminders from phone.\n\nDemo:\n${l}?ref=instagram`,
  email:     (l) =>
    `Namaste,\n\nI wanted to share a quick TrainAxis demo with you.\n\nTrainAxis helps clubs manage students, attendance, monthly fees, QR payment proof, parent reminders, belt progress, expenses, and reports from one simple platform.\n\nDemo:\n${l}?ref=email\n\nIf this looks useful, we can help set up your club and show you how it works.\n\nRegards,\nTrainAxis Team`,
  direct:    (l) =>
    `TrainAxis demo for managing students, attendance, fees, QR payments, parent reminders and reports:\n${l}?ref=direct`,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function demoUrl(slug) {
  return `${window.location.origin}/demo/${slug}`;
}

function StatusPill({ status }) {
  const map = {
    trialing:      { bg: 'rgba(96,165,250,0.12)', text: '#60a5fa', label: 'Trialing'  },
    active:        { bg: 'rgba(52,211,153,0.12)', text: '#34d399', label: 'Active'    },
    trial_expired: { bg: 'rgba(252,211,77,0.12)', text: '#fcd34d', label: 'Expired'   },
    // References the brand-primary token (not a hardcoded hex) so it can't silently drift if
    // the brand red is ever retuned — suspended genuinely is a danger state, unlike the
    // Owner/role badges elsewhere that were misusing this same red for neutral info.
    suspended:     { bg: 'rgb(var(--color-primary-rgb) / 0.12)', text: 'var(--color-primary)', label: 'Suspended' },
    cancelled:     { bg: 'rgba(107,101,96,0.2)',  text: '#8a847c', label: 'Cancelled' },
  };
  const s = map[status] || { bg: 'rgba(107,101,96,0.2)', text: '#8a847c', label: status || '—' };
  return (
    <span style={{
      background: s.bg, color: s.text,
      borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

function BoolDot({ on }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      color: on ? '#34d399' : '#6b6560', fontSize: 11, fontWeight: 600,
    }}>
      {on
        ? <MdCheckCircle style={{ color: '#34d399' }} />
        : <MdCancel     style={{ color: '#6b6560' }} />}
      {on ? 'Yes' : 'No'}
    </span>
  );
}

// ── Row actions ───────────────────────────────────────────────────────────────
function ClubActions({ club, onRefresh }) {
  const queryClient = useQueryClient();
  const navigate    = useNavigate();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['sa-demo-links'] });
    onRefresh();
  };

  const { mutate: doLoad, isPending: loading } = useMutation({
    mutationFn: () => saApi.post(`/api/super-admin/clubs/${club._id}/demo/load`).then(r => r.data),
    onSuccess:  () => { toast.success('Sample data loaded'); invalidate(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to load'),
  });

  const { mutate: doRemove, isPending: removing } = useMutation({
    mutationFn: () => saApi.post(`/api/super-admin/clubs/${club._id}/demo/remove`).then(r => r.data),
    onSuccess:  () => { toast.success('Sample data removed'); invalidate(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to remove'),
  });

  const { mutate: doEnable, isPending: enabling } = useMutation({
    mutationFn: () => saApi.post(`/api/super-admin/clubs/${club._id}/demo/enable`).then(r => r.data),
    onSuccess:  () => { toast.success('Public link enabled'); invalidate(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to enable'),
  });

  const { mutate: doDisable, isPending: disabling } = useMutation({
    mutationFn: () => saApi.post(`/api/super-admin/clubs/${club._id}/demo/disable`).then(r => r.data),
    onSuccess:  () => { toast.success('Public link disabled'); invalidate(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to disable'),
  });

  const copyLink = () => {
    navigator.clipboard.writeText(demoUrl(club.slug))
      .then(() => toast.success('Demo link copied!'))
      .catch(() => toast.error('Copy failed'));
  };

  const btn = (label, onClick, disabled, style = {}) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: '#28261f', color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.08)',
        borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-wrap gap-1.5">
      {!club.demoDataLoaded
        ? btn(loading ? 'Loading…' : 'Load Data', () => {
            if (window.confirm(`Load sample data into "${club.name}"?`)) doLoad();
          }, loading, { background: 'var(--color-primary)', color: '#fff', border: '1px solid var(--color-primary-dark)' })
        : btn(removing ? 'Removing…' : 'Remove Data', () => {
            if (window.confirm(`Remove sample data from "${club.name}"?`)) doRemove();
          }, removing, { background: 'rgb(var(--color-primary-rgb) / 0.08)', color: 'var(--color-primary)', border: '1px solid rgb(var(--color-primary-rgb) / 0.2)' })
      }

      {club.demoDataLoaded && !club.demoPublicEnabled &&
        btn(enabling ? 'Enabling…' : 'Enable Link', () => doEnable(), enabling, { background: 'rgba(52,211,153,0.12)', color: '#34d399', border: '1px solid rgba(52,211,153,0.2)' })
      }
      {club.demoPublicEnabled &&
        btn(disabling ? 'Disabling…' : 'Disable Link', () => doDisable(), disabling)
      }

      {club.demoPublicEnabled && (
        <>
          <button
            onClick={copyLink}
            title="Copy demo link"
            style={{ background: '#28261f', color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <MdContentCopy style={{ fontSize: 12 }} />
            Copy Link
          </button>
          <a
            href={demoUrl(club.slug)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ background: '#28261f', color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}
          >
            <MdOpenInNew style={{ fontSize: 12 }} />
            Open
          </a>
        </>
      )}

      {club.totalLeads > 0 && (
        <button
          onClick={() => navigate(`/superadmin/leads?demoSlug=${club.slug}`)}
          style={{ background: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <MdPeople style={{ fontSize: 12 }} />
          {club.newLeads > 0 ? `${club.newLeads} new · ${club.totalLeads} total` : `${club.totalLeads} leads`}
        </button>
      )}
    </div>
  );
}

// ── Mobile card ───────────────────────────────────────────────────────────────
function ClubCard({ club, onRefresh }) {
  return (
    <div style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 12, padding: 16 }} className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-[#ede8e0] text-sm truncate">{club.name}</p>
          <p style={{ color: '#6b6560', fontSize: 11, fontFamily: 'monospace' }}>{club.slug}</p>
        </div>
        <StatusPill status={club.status} />
      </div>
      <div className="flex flex-wrap gap-3 text-xs" style={{ color: '#8a847c' }}>
        <span>Demo data: <BoolDot on={club.demoDataLoaded} /></span>
        <span>Public link: <BoolDot on={club.demoPublicEnabled} /></span>
      </div>
      {club.lastLeadAt && (
        <p style={{ fontSize: 11, color: '#6b6560' }}>Last lead: {fmtDate(club.lastLeadAt)}</p>
      )}
      <ClubActions club={club} onRefresh={onRefresh} />
    </div>
  );
}

// ── Official demo card ────────────────────────────────────────────────────────
const MAIN_SLUG = 'trainaxis-demo';

function PrepareMainDemoCard({ clubs, onRefresh }) {
  const queryClient  = useQueryClient();
  const [prepared, setPrepared] = useState(false);
  const qrWrapperRef = useRef(null);

  const existingDemo = clubs.find(c => c.slug === MAIN_SLUG);
  const isLive       = existingDemo?.demoPublicEnabled || prepared;
  const link         = demoUrl(MAIN_SLUG);

  const { mutate: prepare, isPending } = useMutation({
    mutationFn: () => saApi.post('/api/super-admin/demo-links/prepare-main').then(r => r.data),
    onSuccess:  () => {
      toast.success('Official demo is live!');
      setPrepared(true);
      queryClient.invalidateQueries({ queryKey: ['sa-demo-links'] });
      onRefresh();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to prepare demo'),
  });

  const downloadQR = () => {
    const canvas = qrWrapperRef.current?.querySelector('canvas');
    if (!canvas) return;
    try {
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = 'trainaxis-demo-qr.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      toast.error('Download failed — try a different browser');
    }
  };

  return (
    <div style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 12, padding: '20px', marginBottom: 20 }}>

      {/* Title row */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <MdRocketLaunch style={{ color: '#c8102e', fontSize: 16, flexShrink: 0 }} />
          <span className="font-bold text-[#ede8e0] text-sm">Official Sales Demo</span>
          {isLive ? (
            <span style={{ background: 'rgba(52,211,153,0.12)', color: '#34d399', borderRadius: 4, padding: '1px 8px', fontSize: 10, fontWeight: 700 }}>LIVE</span>
          ) : existingDemo ? (
            <span style={{ background: 'rgba(252,211,77,0.12)', color: '#fcd34d', borderRadius: 4, padding: '1px 8px', fontSize: 10, fontWeight: 700 }}>NOT READY</span>
          ) : null}
        </div>
        <button
          onClick={() => prepare()}
          disabled={isPending}
          style={{
            background: isLive ? '#28261f' : '#c8102e',
            color:      isLive ? '#8a847c' : '#fff',
            border:     isLive ? '1px solid rgba(237,232,224,0.08)' : 'none',
            borderRadius: 8, padding: isLive ? '6px 12px' : '8px 16px',
            fontSize: isLive ? 11 : 12, fontWeight: 700,
            cursor: isPending ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap', flexShrink: 0, opacity: isPending ? 0.7 : 1,
          }}
        >
          {isPending ? 'Preparing…' : isLive ? 'Re-prepare' : 'Prepare Main Demo'}
        </button>
      </div>

      {/* Description */}
      <p style={{ color: '#8a847c', fontSize: 12, marginBottom: isLive ? 16 : 0 }}>
        One canonical TrainAxis demo link. Creates or reuses the{' '}
        <span style={{ fontFamily: 'monospace' }}>trainaxis-demo</span> club — idempotent, safe to run again.
      </p>

      {/* Live state: analytics mini-row */}
      {isLive && existingDemo && (
        <div className="flex flex-wrap gap-5 mt-3 mb-4 pt-3" style={{ borderTop: '1px solid rgba(237,232,224,0.08)' }}>
          {[
            { label: 'Views (7d)',   value: existingDemo.views7d    || 0, color: '#a78bfa' },
            { label: 'Total Views',  value: existingDemo.totalViews || 0, color: '#ede8e0' },
            { label: 'Leads',        value: existingDemo.totalLeads || 0, color: '#60a5fa' },
            ...(existingDemo.totalViews > 0 ? [{
              label: 'Conversion',
              value: `${Math.round((existingDemo.totalLeads / existingDemo.totalViews) * 100)}%`,
              color: '#34d399',
            }] : []),
          ].map(({ label, value, color }) => (
            <div key={label}>
              <p style={{ color: '#6b6560', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
              <p style={{ color, fontWeight: 800, fontSize: 18, lineHeight: 1.2 }}>{value}</p>
            </div>
          ))}
          {existingDemo.totalViews === 0 && (
            <p style={{ color: '#6b6560', fontSize: 11, alignSelf: 'center' }}>
              Views will appear after someone opens the demo link
            </p>
          )}
        </div>
      )}

      {/* Live state: link row + QR + share templates */}
      {isLive && (
        <>
          <div className="flex gap-6 flex-wrap">

            {/* Left: URL and action buttons */}
            <div className="flex-1 min-w-0 space-y-3">
              <div style={{ background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 8, padding: '8px 12px', fontFamily: 'monospace', fontSize: 12, color: '#c5bfb5', wordBreak: 'break-all' }}>
                {link}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => { navigator.clipboard.writeText(link); toast.success('Demo link copied!'); }}
                  style={{ background: '#28261f', color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.10)', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                >
                  <MdContentCopy style={{ fontSize: 14 }} /> Copy Link
                </button>
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ background: '#28261f', color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.10)', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none' }}
                >
                  <MdOpenInNew style={{ fontSize: 14 }} /> Open Demo
                </a>
              </div>
            </div>

            {/* Right: QR code card */}
            <div style={{ flexShrink: 0 }}>
              <div style={{ background: '#fff', borderRadius: 12, padding: '12px 12px 8px', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.4)', width: 172 }}>
                <div ref={qrWrapperRef}>
                  <QRCodeCanvas
                    value={`${link}?ref=qr`}
                    size={148}
                    bgColor="#ffffff"
                    fgColor="#111827"
                    level="M"
                    includeMargin={false}
                  />
                </div>
                <p style={{ marginTop: 6, fontSize: 9, fontWeight: 800, color: '#111827', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  TrainAxis
                </p>
                <p style={{ fontSize: 8, color: '#6b6560', marginTop: 1, marginBottom: 6, fontFamily: 'monospace' }}>
                  trainaxis-demo
                </p>
              </div>
              <button
                onClick={downloadQR}
                style={{
                  marginTop: 8, width: '100%',
                  background: '#28261f', color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.10)',
                  borderRadius: 7, padding: '6px 0', fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                <MdDownload style={{ fontSize: 14 }} /> Download QR
              </button>
            </div>

          </div>

          {/* Share Templates */}
          <div style={{ borderTop: '1px solid rgba(237,232,224,0.08)', marginTop: 16, paddingTop: 16 }}>
            <p style={{ color: '#6b6560', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Share Templates
            </p>
            <div className="flex flex-wrap gap-2">

              {/* WhatsApp */}
              <button
                onClick={() => navigator.clipboard.writeText(TEMPLATES.whatsapp(link)).then(() => toast.success('WhatsApp message copied!')).catch(() => toast.error('Copy failed'))}
                style={{ background: '#064e3b', color: '#6ee7b7', border: '1px solid #065f46', borderRadius: 7, padding: '6px 11px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <FaWhatsapp style={{ fontSize: 12 }} /> Copy WhatsApp
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(TEMPLATES.whatsapp(link))}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: '#052e16', color: '#86efac', border: '1px solid #166534', borderRadius: 7, padding: '6px 11px', fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none' }}
              >
                <FaWhatsapp style={{ fontSize: 12 }} /> Open WhatsApp
              </a>

              {/* Facebook */}
              <button
                onClick={() => navigator.clipboard.writeText(TEMPLATES.facebook(link)).then(() => toast.success('Facebook message copied!')).catch(() => toast.error('Copy failed'))}
                style={{ background: '#1e3a5f', color: '#93c5fd', border: '1px solid #1e40af', borderRadius: 7, padding: '6px 11px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <FaFacebook style={{ fontSize: 12 }} /> Copy Facebook
              </button>

              {/* Instagram DM */}
              <button
                onClick={() => navigator.clipboard.writeText(TEMPLATES.instagram(link)).then(() => toast.success('Instagram DM copied!')).catch(() => toast.error('Copy failed'))}
                style={{ background: '#4c1d95', color: '#c4b5fd', border: '1px solid #5b21b6', borderRadius: 7, padding: '6px 11px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <FaInstagram style={{ fontSize: 12 }} /> Copy Instagram DM
              </button>

              {/* Email */}
              <button
                onClick={() => navigator.clipboard.writeText(`Subject: ${EMAIL_SUBJECT}\n\n${TEMPLATES.email(link)}`).then(() => toast.success('Email copied!')).catch(() => toast.error('Copy failed'))}
                style={{ background: '#28261f', color: '#ede8e0', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 7, padding: '6px 11px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <MdEmail style={{ fontSize: 12 }} /> Copy Email
              </button>
              <a
                href={`mailto:?subject=${encodeURIComponent(EMAIL_SUBJECT)}&body=${encodeURIComponent(TEMPLATES.email(link))}`}
                style={{ background: '#1a1918', color: '#8a847c', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 7, padding: '6px 11px', fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none' }}
              >
                <MdEmail style={{ fontSize: 12 }} /> Open Email
              </a>

              {/* Short / direct */}
              <button
                onClick={() => navigator.clipboard.writeText(TEMPLATES.direct(link)).then(() => toast.success('Short message copied!')).catch(() => toast.error('Copy failed'))}
                style={{ background: '#28261f', color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 7, padding: '6px 11px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <MdContentCopy style={{ fontSize: 12 }} /> Copy Short Message
              </button>

            </div>
            <p style={{ color: '#6b6560', fontSize: 10, marginTop: 8 }}>
              Messages are copied to clipboard — paste and send manually. Nothing is auto-sent.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DemoLinks() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const { data, isPending, isError, refetch } = useQuery({
    queryKey:  ['sa-demo-links'],
    queryFn:   () => saApi.get('/api/super-admin/demo-links').then(r => r.data),
    staleTime: 30 * 1000,
  });

  const clubs = (data?.clubs || []).filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.slug.includes(search.toLowerCase())
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['sa-demo-links'] });

  return (
    <div className="p-5 max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-bold text-[#ede8e0]">Demo Links</h1>
          <p className="text-xs mt-0.5" style={{ color: '#6b6560' }}>
            {data?.clubs?.length || 0} clubs · Manage public demo links and sample data
          </p>
        </div>
        <button
          onClick={() => refetch()}
          style={{ background: '#28261f', color: '#8a847c', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
        >
          <MdRefresh /> Refresh
        </button>
      </div>

      {/* Official Sales Demo quick-action card */}
      <PrepareMainDemoCard clubs={data?.clubs || []} onRefresh={invalidate} />

      {/* Workflow banner */}
      <div style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
        <div className="flex items-start gap-2">
          <MdInfo style={{ color: '#60a5fa', fontSize: 16, flexShrink: 0, marginTop: 1 }} />
          <div>
            <p style={{ color: '#93c5fd', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
              Public demo links are controlled by TrainAxis — clubs are customers, not sales agents
            </p>
            <p style={{ color: '#60a5fa', fontSize: 11 }}>
              Sales workflow: <strong style={{ color: '#bfdbfe' }}>1. Load data</strong> → <strong style={{ color: '#bfdbfe' }}>2. Enable link</strong> → <strong style={{ color: '#bfdbfe' }}>3. Copy &amp; share</strong> → <strong style={{ color: '#bfdbfe' }}>4. Track leads</strong> → <strong style={{ color: '#bfdbfe' }}>5. Follow up in Sales Leads</strong>
            </p>
          </div>
        </div>
      </div>

      {/* All-clubs table */}
      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search clubs…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            background: '#1e1d1b', color: '#ede8e0', border: '1px solid rgba(237,232,224,0.08)',
            borderRadius: 8, padding: '7px 12px', fontSize: 13, outline: 'none', width: '100%', maxWidth: 320,
          }}
        />
      </div>

      {/* Loading */}
      {isPending && (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 10, height: 52 }} className="animate-pulse" />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="text-center py-12" style={{ color: 'var(--color-primary)' }}>
          Failed to load demo links.
          <button onClick={() => refetch()} style={{ marginLeft: 8, textDecoration: 'underline', background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      {/* Empty */}
      {!isPending && !isError && clubs.length === 0 && (
        <div className="text-center py-16" style={{ color: '#6b6560' }}>
          <p className="font-semibold" style={{ color: '#8a847c' }}>No clubs found</p>
        </div>
      )}

      {/* Desktop table */}
      {!isPending && !isError && clubs.length > 0 && (
        <>
          <div className="hidden lg:block overflow-x-auto rounded-xl" style={{ border: '1px solid rgba(237,232,224,0.08)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
                  {['Club', 'Slug', 'Plan', 'Demo Data', 'Public Link', 'Views', 'Leads', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#6b6560', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#1a1918', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clubs.map(club => (
                  <tr key={club._id} style={{ borderBottom: '1px solid rgba(237,232,224,0.05)' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <p className="font-semibold text-[#ede8e0]">{club.name}</p>
                      <StatusPill status={club.status} />
                    </td>
                    <td style={{ padding: '12px 14px', color: '#8a847c', fontFamily: 'monospace', fontSize: 12 }}>{club.slug}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ background: 'rgba(96,165,250,0.12)', color: '#60a5fa', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700 }}>
                        {club.subscriptionPlan || 'free'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}><BoolDot on={club.demoDataLoaded} /></td>
                    <td style={{ padding: '12px 14px' }}><BoolDot on={club.demoPublicEnabled} /></td>
                    <td style={{ padding: '12px 14px' }}>
                      {club.totalViews > 0 ? (
                        <div>
                          <p className="font-semibold text-[#ede8e0]">{club.totalViews}</p>
                          <p style={{ color: '#a78bfa', fontSize: 11 }}>{club.views7d} this week</p>
                          {sortedSources(club.sourceBreakdown).slice(0, 3).map(([src, cnt]) => (
                            <p key={src} style={{ color: '#6b6560', fontSize: 10, marginTop: 1 }}>
                              {SOURCE_LABEL[src] || src}: {cnt}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#6b6560' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {club.totalLeads > 0 ? (
                        <div>
                          <p className="font-semibold text-[#ede8e0]">{club.totalLeads}</p>
                          {club.newLeads > 0 && (
                            <p style={{ color: '#60a5fa', fontSize: 11 }}>{club.newLeads} new</p>
                          )}
                          {club.totalViews > 0 && (
                            <p style={{ color: '#34d399', fontSize: 10 }}>
                              {Math.round((club.totalLeads / club.totalViews) * 100)}% conv.
                            </p>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#6b6560' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <ClubActions club={club} onRefresh={invalidate} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden space-y-3">
            {clubs.map(club => (
              <ClubCard key={club._id} club={club} onRefresh={invalidate} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
